// React Query hooks for Firestore operations
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  blogPostsService,
  infographicPostsService,
  conferencesService,
  projectsService,
  videosService,
  podcastPodsService,
  booksService,
  quotesService,
  talksService,
  personalInfoService,
  servicesService,
  firestoreService
} from '../config/firestore';

// Query keys for consistent cache management
export const QUERY_KEYS = {
  BLOG_POSTS: 'blog-posts',
  INFOGRAPHIC_POSTS: 'infographic-posts',
  CONFERENCES: 'conferences',
  PROJECTS: 'projects', 
  VIDEOS: 'videos',
  PODCAST_PODS: 'podcast-pods',
  BOOKS: 'books',
  QUOTES: 'quotes',
  TALKS: 'talks',
  PERSONAL_INFO: 'personal-info',
  SERVICES: 'services'
};

// Blog Posts hooks
export const useBlogPosts = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.BLOG_POSTS],
    queryFn: () => blogPostsService.getAllBlogPosts(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

// Infographic Posts hooks
export const useInfographicPosts = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.INFOGRAPHIC_POSTS],
    queryFn: () => infographicPostsService.getAllInfographicPosts(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

// Podcast Pods hooks
export const usePodcastPods = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PODCAST_PODS],
    queryFn: () => podcastPodsService.getAllPodcastPods(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

// Talks hooks
export const useTalks = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TALKS],
    queryFn: () => talksService.getAllTalks(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

// Portfolio hooks - already updated above

// Portfolio specific hooks - updated for new collection structure
export const useProjects = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PROJECTS],
    queryFn: () => projectsService.getAllProjects(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

export const useConferences = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.CONFERENCES],
    queryFn: () => conferencesService.getAllConferences(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

export const useVideos = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.VIDEOS],
    queryFn: () => videosService.getAllVideos(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

// Content Hub hooks
export const useBooks = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.BOOKS],
    queryFn: () => booksService.getAllBooks(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

export const useQuotes = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.QUOTES],
    queryFn: () => quotesService.getAllQuotes(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

// Personal Info hooks
export const usePersonalInfo = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PERSONAL_INFO],
    queryFn: () => personalInfoService.getPersonalInfo(),
    staleTime: 10 * 60 * 1000, // 10 minutes (personal info changes less frequently)
    cacheTime: 30 * 60 * 1000, // 30 minutes
    ...options
  });
};

export const usePersonalInfoBySection = (section, options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PERSONAL_INFO, section],
    queryFn: () => personalInfoService.getPersonalInfoBySection(section),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    enabled: !!section,
    ...options
  });
};

// Services hooks
export const useServices = (options = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SERVICES],
    queryFn: () => servicesService.getAllServices(),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    ...options
  });
};

// Mutation hooks for admin operations (future use)
export const useCreatePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (postData) => firestoreService.add('posts', postData),
    onSuccess: () => {
      // Invalidate and refetch posts queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BLOG_POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PODCAST_POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TALK_POSTS] });
    }
  });
};

export const useUpdatePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => firestoreService.update('posts', id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific post and all posts queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POST_BY_SLUG] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BLOG_POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PODCAST_POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TALK_POSTS] });
    }
  });
};

export const useDeletePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => firestoreService.delete('posts', id),
    onSuccess: () => {
      // Invalidate all posts queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BLOG_POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PODCAST_POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TALK_POSTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POST_BY_SLUG] });
    }
  });
};

// Generic hooks for any collection (for admin use)
export const useCollection = (collectionName, options = {}) => {
  return useQuery({
    queryKey: [collectionName],
    queryFn: () => firestoreService.getAll(collectionName, options.firestoreOptions),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    enabled: !!collectionName,
    ...options
  });
};

export const useDocument = (collectionName, id, options = {}) => {
  return useQuery({
    queryKey: [collectionName, id],
    queryFn: () => firestoreService.getById(collectionName, id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    enabled: !!(collectionName && id),
    ...options
  });
};


